// browser-image-compression（约 50KB）只在用户真的选了图才需要，按需加载，
// 别让它跟着 AiImageGeneratorClient 进每个生成页的首屏 JS
const loadImageCompression = () =>
	import("browser-image-compression").then((mod) => mod.default);

export interface CompressionOptions {
	maxSizeMB?: number;
	maxWidthOrHeight?: number;
	useWebWorker?: boolean;
	fileType?: string;
	initialQuality?: number;
}

export const defaultCompressionOptions: CompressionOptions = {
	maxSizeMB: 2, // 压缩目标大小 2MB
	maxWidthOrHeight: 1280, // 分辨率上限 1280px（长边）
	useWebWorker: true, // 使用 Web Worker 进行压缩以避免阻塞 UI
	fileType: "image/webp", // 强制转码为 WebP 格式
	initialQuality: 0.8, // 初始质量设置为 0.8
};

/**
 * 压缩图片文件
 * @param file 要压缩的图片文件
 * @param options 压缩选项
 * @returns 压缩后的文件
 */
export async function compressImage(
	file: File,
	options: CompressionOptions = {},
): Promise<File> {
	// Invalid input must not reach the compression fallback and get uploaded unchanged.
	if (!file.type.startsWith("image/")) {
		throw new Error("File is not an image");
	}
	const dimensions = await getImageDimensions(file);

	// 合并默认选项和用户选项
	const compressionOptions = {
		...defaultCompressionOptions,
		...options,
	};

	try {
		// 智能跳过压缩：仅当文件同时满足大小、尺寸和格式要求时跳过压缩
		// 条件：< 2MB 且 ≤ 1280px 且已是 WebP 格式
		const fileSizeMB = file.size / 1024 / 1024;
		if (fileSizeMB < 2 && file.type === "image/webp") {
			// 检查图片尺寸
			const maxDimension = compressionOptions.maxWidthOrHeight || 1280;

			if (
				dimensions.width <= maxDimension &&
				dimensions.height <= maxDimension
			) {
				// 文件已满足所有要求，直接返回原文件
				return file;
			}
		}

		// 执行压缩、缩放和格式转换
		const imageCompression = await loadImageCompression();
		const compressedFile = await imageCompression(file, compressionOptions);

		// 确保压缩后的文件有正确的名称和类型
		const renamedFile = new File([compressedFile], file.name, {
			type: compressedFile.type || file.type,
			lastModified: Date.now(),
		});

		return renamedFile;
	} catch (error) {
		console.error("Image compression failed:", error);
		// 如果压缩失败，返回原文件
		return file;
	}
}

/**
 * 获取图片尺寸
 * @param file 图片文件
 * @returns 图片的宽度和高度
 */
function getImageDimensions(
	file: File,
): Promise<{ width: number; height: number }> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const url = URL.createObjectURL(file);

		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve({
				width: img.naturalWidth,
				height: img.naturalHeight,
			});
		};

		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error("Failed to load image"));
		};

		img.src = url;
	});
}

/**
 * 压缩图片并转换为Base64
 * @param file 要压缩的图片文件
 * @param options 压缩选项
 * @returns 包含base64字符串和压缩信息的对象
 */
export async function compressToBase64(
	file: File,
	options: CompressionOptions = {},
): Promise<{
	base64: string;
	compressionInfo: {
		originalSize: number;
		compressedSize: number;
		compressionRatio: number;
	};
}> {
	const originalSize = file.size;

	// 压缩图片
	const compressedFile = await compressImage(file, options);
	const compressedSize = compressedFile.size;

	// 转换为Base64
	const base64 = await convertFileToBase64(compressedFile);

	return {
		base64: `data:${compressedFile.type};base64,${base64}`,
		compressionInfo: {
			originalSize,
			compressedSize,
			compressionRatio: (originalSize - compressedSize) / originalSize,
		},
	};
}

/**
 * 将文件转换为Base64字符串
 * @param file 要转换的文件
 * @returns Base64字符串（不包含data URL前缀）
 */
function convertFileToBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			const base64String = reader.result as string;
			const base64 = base64String.split(",")[1];
			resolve(base64);
		};
		reader.onerror = (error) => reject(error);
	});
}

/**
 * 格式化文件大小显示
 * @param bytes 字节数
 * @returns 格式化的文件大小字符串
 */
export function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}
